import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ApplicationEntryLayout } from '../components/ApplicationEntryLayout/ApplicationEntryLayout';
import ValidationGroup from 'devextreme-react/validation-group';
import { useValidateAndSaveApplication } from '../hooks/useValidateAndSaveApplication';
import { Button, ButtonTheme, useWaitOverlayStore } from '@apply/components';
import { OtherAmount as LandCostOtherAmount } from './components/LandCost/components/OtherAmountLand';
import { OtherDescription as LandCostOtherDescription } from './components/LandCost/components/OtherDescriptionLand';
import { OtherAmount as SetupOtherAmount } from './components/Setup/components/OtherAmount';
import { OtherDescription as SetupOtherDescription } from './components/Setup/components/OtherDescription';
import { OtherAmount as UtilitiesOtherAmount } from './components/Utilities/components/OtherAmount';
import { OtherDescription as UtilitiesOtherDescription } from './components/Utilities/components/OtherDescription';
import { OtherAmount as SiteWorkLandOtherAmount } from './components/SiteWorkLand/components/OtherAmount';
import { OtherDescription as SiteWorkLandOtherDescription } from './components/SiteWorkLand/components/OtherDescription';
import { LandCost } from './components/LandCost/components/LandCostLand';
import { DemolitionLand } from './components/SiteWork/components/DemolitionLand';
import { Landscaping } from './components/SiteWork/components/Landscaping';
import { GovernmentalFees } from './components/Setup/components/GovernmentalFees';
import { Engineering } from './components/Setup/components/Engineering';
import { ElectricLines } from './components/Utilities/components/ElecticLines';
import { GasLines } from './components/Utilities/components/GasLines';
import { Sprinklers } from './components/Utilities/components/Sprinklers';
import { TelephoneCableFiberOptic } from './components/Utilities/components/TelephoneCableFiberOptic';
import { Clearing } from './components/SiteWorkLand/components/Clearing';
import { Grading } from './components/SiteWorkLand/components/Grading';
import { Paving } from './components/SiteWorkLand/components/Paving';
import { Sidewalks } from './components/SiteWorkLand/components/Sidewalks';
import { Curbs } from './components/SiteWorkLand/components/Curbs';
import { Gutters } from './components/SiteWorkLand/components/Gutters';
import { Sewers } from './components/SiteWorkLand/components/Sewers';
import { Hardscaping } from './components/SiteWorkLand/components/Hardscaping';
import { ApplyApi, IBudgetLand } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../../ApplicationInsights';
import '../scss/_budgetPage.scss';
import Validator, { CustomRule } from 'devextreme-react/validator';
import DxTextBox from 'devextreme-react/text-box';
import ValidationSummary from 'devextreme-react/validation-summary';
import validationEngine from 'devextreme/ui/validation_engine';
import { useSaveApplicationWithoutValidation } from '../hooks/useSaveApplicationWithoutValidation';
import { useRedirectIfComplete } from '../hooks/useRedirectIfApplicationComplete';
import { CurrentApplicationPage, useNavigateApplication } from '../hooks/useNavigateApplication';
import { budgetAndSpecSheetNameBuilder } from '../constants/budgetAndSpecSheetNameBuilder';
import { usePausePointTracker } from '../hooks/usePausePointTracker.tsx';
import { sumBudgetNumbers } from './functions/sumBudgetNumbers.ts';
import { BudgetTotal } from './components/BudgetTotal.tsx';
import { useApplicationQuery } from '../../queries/applicationQueries';
import { useApplicationPropertyForms } from '../../queries/BorrowerFields/ApplicationFields';
import { usePropertyStreet1, usePropertyStreet2 } from '../../queries/usePropertyCoreFields';
import { usePropertyLandBudgetFile } from '../../queries/PropertyFileFields/NonRentalFiles';
import { useBudgetLandCache } from './queries/useBudgetLandCache';
import { ApplicationCrumbType } from '../components/ApplicationEntryLayout/components/ApplicationEntrySections/types/ApplicationCrumbType.tsx';

interface BudgetLandPageParameters {
    printMode?: boolean;
}

export const BudgetLandPage: React.FC<BudgetLandPageParameters> = ({ printMode = false }) => {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const { search } = useLocation();
    const propertyGuid = new URLSearchParams(search).get('pid') || '';

    const token = new URLSearchParams(search).get('token');
    const { isPending } = useApplicationQuery(entryGuid, token);

    const applyApi = new ApplyApi(applicationInsights);
    const BUDGET_GROUP = 'budget-min-total';
    const validationGroupRef = useRef(null);
    const { validateAndSaveApplication } = useValidateAndSaveApplication(validationGroupRef);
    const { saveApplicationNoValidation } = useSaveApplicationWithoutValidation();
    const navigate = useNavigate();
    const budget = useBudgetLandCache(propertyGuid);
    const { handleMode: budgetHandleMode, setSavedFormId } = usePropertyLandBudgetFile(propertyGuid);
    const [propertyName] = usePropertyStreet1(propertyGuid);
    const [propertyName2] = usePropertyStreet2(propertyGuid);
    const [forms, setForms] = useApplicationPropertyForms();
    const { navigateApplicationForward, navigateApplicationBackward } = useNavigateApplication({
        page: CurrentApplicationPage.Budget,
        entryGuid,
        propertyGuid,
    });
    const { updatePausePoint, clearPausePoint } = usePausePointTracker();

    const newLandBudgetSelected = budgetHandleMode === 'createnewform';
    const savedLandBudgetSelected = budgetHandleMode === 'editsavedform';

    useRedirectIfComplete(printMode);

    useEffect(() => {
        if (!isPending) {
            useWaitOverlayStore.getState().setWaiting(false);
        }
    }, [isPending]);

    useEffect(() => {
        if (!entryGuid || savedLandBudgetSelected) return;
        const currentForms = forms ?? [];
        const exists = currentForms.some((f) => f.type === 'Land Budget' && f.propertyGuid === propertyGuid);
        if (exists) return;
        const stub: IBudgetLand = {
            id: 0,
            guid: entryGuid,
            type: 'Land Budget' as IBudgetLand['type'],
            name: '',
            propertyGuid,
            values: {} as IBudgetLand['values'],
        };
        setForms([...currentForms, stub]);
    }, [entryGuid, savedLandBudgetSelected]);

    useEffect(() => {
        if (!entryGuid) return;
        const currentForms = forms ?? [];
        const fullPN = budgetAndSpecSheetNameBuilder(propertyName ?? '', propertyName2 ?? '');
        const idx = currentForms.findIndex((f) => f.type === 'Land Budget' && f.propertyGuid === propertyGuid);
        if (idx === -1) return;
        const newForms = [...currentForms];
        newForms[idx] = { ...newForms[idx], name: fullPN };
        setForms(newForms);
    }, [propertyName, propertyName2]);

    useEffect(() => {
        if (printMode) document.body.style.backgroundColor = '#fff';
    }, [printMode]);

    if (isPending && entryGuid) {
        return null;
    }

    const onSaveAndContinue = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        const result = validationEngine.validateGroup(BUDGET_GROUP);
        if (!result?.isValid) return;

        clearPausePoint();

        const applicationResponse = await validateAndSaveApplication();
        if (!applicationResponse) return;

        const savedFormResponse = await applyApi.updateSharedForm(budget);
        if (savedFormResponse?.result) setSavedFormId(savedFormResponse.result);

        navigateApplicationForward();
    };

    const onSaveAndExit = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        updatePausePoint();

        const applicationResponse = await saveApplicationNoValidation();
        if (!applicationResponse) return;

        const savedFormResponse = await applyApi.updateSharedForm(budget);
        if (savedFormResponse?.result) setSavedFormId(savedFormResponse.result);

        navigate('/home');
    };

    const onChangeAutofill = async () => {
        await validateAndSaveApplication();
        navigate(`/budget/selectbudget/${entryGuid}?pid=${propertyGuid}`);
    };

    const nextStepButton = {
        id: 'continueApplicationButton',
        dataTestid: 'continueApplicationButton',
        children: 'Continue',
        handleClick: onSaveAndContinue,
    };

    const saveAndExitButton = {
        id: 'saveAndExitApplicationButton',
        dataTestid: 'saveAndExitApplicationButton',
        children: 'Save and Exit',
        handleClick: onSaveAndExit,
    };

    const previousStepButtonClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        clearPausePoint();

        const applicationResponse = await saveApplicationNoValidation();
        if (!applicationResponse) return;

        navigateApplicationBackward();
    };

    return (
        <ValidationGroup
            ref={validationGroupRef}
            className={printMode ? 'print' : ''}
        >
            <ApplicationEntryLayout
                currentCrumb={ApplicationCrumbType.Budget}
                saveAndExitButtonProps={saveAndExitButton}
                nextStepButtonProps={nextStepButton}
                previousStepButtonVisible={true}
                previousStepButtonClick={previousStepButtonClick}
                propertyAddress={propertyName}
                printMode={printMode}
            >
                <div>
                    {!printMode && !newLandBudgetSelected ? (
                        <Button
                            id='tertiaryBudgetButton'
                            theme={ButtonTheme.TertiaryBudget}
                            handleClick={onChangeAutofill}
                            children={
                                <div className='autofill'>
                                    Change autofill selection
                                    <img
                                        src='/assets/img/change-autofill.png'
                                        alt='Change Autofill Selection'
                                        height='100%'
                                    />
                                </div>
                            }
                        ></Button>
                    ) : (
                        <h1>Budget</h1>
                    )}
                    <div>
                        <h4 className='headers'>Land Cost</h4>
                        <LandCost
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <span className='paired-fields'>
                            <LandCostOtherDescription
                                propertyGuid={propertyGuid}
                                isReview={printMode}
                            />
                            <LandCostOtherAmount
                                propertyGuid={propertyGuid}
                                isReview={printMode}
                            />
                        </span>
                    </div>
                    <div>
                        <h4 className='headers'>Setup</h4>
                        <GovernmentalFees
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Engineering
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <span className='paired-fields'>
                            <SetupOtherDescription
                                propertyGuid={propertyGuid}
                                isReview={printMode}
                            />
                            <SetupOtherAmount
                                propertyGuid={propertyGuid}
                                isReview={printMode}
                            />
                        </span>
                    </div>
                    <div>
                        <h4 className='headers'>Utilities</h4>
                        <ElectricLines
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <GasLines
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Sprinklers
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <TelephoneCableFiberOptic
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <span className='paired-fields'>
                            <UtilitiesOtherDescription
                                propertyGuid={propertyGuid}
                                isReview={printMode}
                            />
                            <UtilitiesOtherAmount
                                propertyGuid={propertyGuid}
                                isReview={printMode}
                            />
                        </span>
                    </div>
                    <div>
                        <h4 className='headers'>Site Work</h4>
                        <DemolitionLand
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Clearing
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Grading
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Paving
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Sidewalks
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Curbs
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Gutters
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Sewers
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Landscaping
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Hardscaping
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <span className='paired-fields'>
                            <SiteWorkLandOtherDescription
                                propertyGuid={propertyGuid}
                                isReview={printMode}
                            />
                            <SiteWorkLandOtherAmount
                                propertyGuid={propertyGuid}
                                isReview={printMode}
                            />
                        </span>
                    </div>
                    <BudgetTotal budget={budget} />
                </div>
                <DxTextBox
                    value=''
                    visible={false}
                >
                    <Validator validationGroup={BUDGET_GROUP}>
                        <CustomRule
                            reevaluate={true}
                            message='Total budget value too low, minimum total budget is $75,000'
                            validationCallback={() => sumBudgetNumbers(budget?.values ?? {}) >= 75000}
                        />
                    </Validator>
                </DxTextBox>
                <div className='validation-summary-center'>
                    <ValidationSummary validationGroup={BUDGET_GROUP} />
                </div>
            </ApplicationEntryLayout>
        </ValidationGroup>
    );
};
